import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getVideosByCategory } from '@/mocks/videos';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'yingshi');
  const currentCategory = getCategoryBySlug(activeTab);
  const categoryVideos = getVideosByCategory(activeTab);

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 精品视频精选平台`,
    description: currentCategory.description,
    url: `https://www.jingpinsp.com/category/${activeTab}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '精品视频精选平台',
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 精品视频精选平台`}
        description={currentCategory.description}
        keywords={`${currentCategory.name},精品视频,高清视频,在线观看`}
        canonicalUrl={`https://www.jingpinsp.com/category/${activeTab}`}
        ogTitle={`${currentCategory.name} - 精品视频精选平台`}
        ogDescription={currentCategory.description}
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center">
                <i className={`${currentCategory.icon} text-primary-500 text-2xl w-6 h-6 flex items-center justify-center`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200/70 hover:border-background-300'
                  }`}
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-900">{categoryVideos.length}</strong> 部精品视频
              </p>
            </div>

            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/50 hover:border-primary-200 transition-all duration-300 no-underline"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                        {video.duration}
                      </div>
                      <div className="absolute top-2 left-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded">
                        {video.quality}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{video.description}</p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                          {(video.views / 10000).toFixed(1)}万
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500 w-3.5 h-3.5 flex items-center justify-center"></i>
                          {video.rating}
                        </span>
                        <span>{video.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-video-line text-foreground-400 text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无内容，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}